import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { 
  Plus, Upload, FileText, Trash2, Edit, Save, BookOpen, 
  CheckCircle, AlertCircle, Loader2, X, File, Image, FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Extended UK Education Stages
export const STAGES = [
  { value: 'year-1', label: 'Year 1 (KS1)', keyStage: 'KS1' },
  { value: 'year-2', label: 'Year 2 (KS1)', keyStage: 'KS1' },
  { value: 'year-3', label: 'Year 3 (KS2)', keyStage: 'KS2' },
  { value: 'year-4', label: 'Year 4 (KS2)', keyStage: 'KS2' },
  { value: 'year-5', label: 'Year 5 (KS2)', keyStage: 'KS2' },
  { value: 'year-6', label: 'Year 6 (KS2)', keyStage: 'KS2' },
  { value: '7plus', label: '7+ Entry', keyStage: 'Entry' },
  { value: '11plus', label: '11+ Entry', keyStage: 'Entry' },
  { value: 'year-7', label: 'Year 7 (KS3)', keyStage: 'KS3' },
  { value: 'year-8', label: 'Year 8 (KS3)', keyStage: 'KS3' },
  { value: 'year-9', label: 'Year 9 (KS3)', keyStage: 'KS3' },
  { value: 'year-10', label: 'Year 10 (GCSE)', keyStage: 'KS4' },
  { value: 'year-11', label: 'Year 11 (GCSE)', keyStage: 'KS4' },
  { value: 'gcse', label: 'GCSE', keyStage: 'KS4' },
  { value: 'year-12', label: 'Year 12 (A-Level)', keyStage: 'KS5' },
  { value: 'year-13', label: 'Year 13 (A-Level)', keyStage: 'KS5' },
  { value: 'a-levels', label: 'A-Levels', keyStage: 'KS5' },
];

// Subjects by Key Stage
export const SUBJECTS_BY_STAGE = {
  // KS1 (Year 1-2)
  'year-1': ['maths', 'english', 'phonics', 'science'],
  'year-2': ['maths', 'english', 'phonics', 'science'],
  
  // KS2 (Year 3-6)
  'year-3': ['maths', 'english', 'science', 'reasoning'],
  'year-4': ['maths', 'english', 'science', 'reasoning'],
  'year-5': ['maths', 'english', 'science', 'reasoning'],
  'year-6': ['maths', 'english', 'science', 'reasoning'],
  
  // Entry Exams
  '7plus': ['maths', 'english', 'reasoning'],
  '11plus': ['maths', 'english', 'verbal-reasoning', 'non-verbal-reasoning'],
  
  // KS3 (Year 7-9)
  'year-7': ['maths', 'english', 'science', 'history', 'geography', 'languages', 'computing'],
  'year-8': ['maths', 'english', 'science', 'history', 'geography', 'languages', 'computing'],
  'year-9': ['maths', 'english', 'science', 'history', 'geography', 'languages', 'computing'],
  
  // KS4 / GCSE (Year 10-11)
  'year-10': ['maths', 'english-lang', 'english-lit', 'biology', 'chemistry', 'physics', 'combined-science', 'history', 'geography', 'french', 'spanish', 'german', 'computer-science', 'business', 'economics', 'art', 'music', 'pe'],
  'year-11': ['maths', 'english-lang', 'english-lit', 'biology', 'chemistry', 'physics', 'combined-science', 'history', 'geography', 'french', 'spanish', 'german', 'computer-science', 'business', 'economics', 'art', 'music', 'pe'],
  'gcse': ['maths', 'english-lang', 'english-lit', 'biology', 'chemistry', 'physics', 'combined-science', 'history', 'geography', 'french', 'spanish', 'german', 'computer-science', 'business', 'economics'],
  
  // KS5 / A-Level (Year 12-13)
  'year-12': ['maths', 'further-maths', 'physics', 'chemistry', 'biology', 'economics', 'business', 'psychology', 'sociology', 'history', 'geography', 'english-lit', 'computer-science', 'art', 'music', 'french', 'spanish', 'german'],
  'year-13': ['maths', 'further-maths', 'physics', 'chemistry', 'biology', 'economics', 'business', 'psychology', 'sociology', 'history', 'geography', 'english-lit', 'computer-science', 'art', 'music', 'french', 'spanish', 'german'],
  'a-levels': ['maths', 'further-maths', 'physics', 'chemistry', 'biology', 'economics', 'business', 'psychology', 'sociology', 'history', 'geography', 'english-lit', 'computer-science'],
};

// Subject labels for display
export const SUBJECT_LABELS = {
  'maths': 'Mathematics',
  'english': 'English',
  'english-lang': 'English Language',
  'english-lit': 'English Literature',
  'phonics': 'Phonics',
  'science': 'Science',
  'biology': 'Biology',
  'chemistry': 'Chemistry',
  'physics': 'Physics',
  'combined-science': 'Combined Science',
  'reasoning': 'Reasoning',
  'verbal-reasoning': 'Verbal Reasoning',
  'non-verbal-reasoning': 'Non-Verbal Reasoning',
  'history': 'History',
  'geography': 'Geography',
  'languages': 'Languages',
  'french': 'French',
  'spanish': 'Spanish',
  'german': 'German',
  'computing': 'Computing',
  'computer-science': 'Computer Science',
  'business': 'Business Studies',
  'economics': 'Economics',
  'psychology': 'Psychology',
  'sociology': 'Sociology',
  'art': 'Art & Design',
  'music': 'Music',
  'pe': 'Physical Education',
  'further-maths': 'Further Mathematics',
};

const QuestionBankForm = ({ 
  userType = 'admin', // 'admin' or 'tutor'
  isVerifiedTutor = false,
  onQuestionAdded,
  editingQuestion = null,
  onCancelEdit
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadMode, setUploadMode] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState({
    stage: '',
    subject: '',
    paperType: 'practice', // 'practice' or 'mock' or 'revision'
    paperNumber: 1,
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
    explanation: '',
    topic: '',
    difficulty: 'medium',
    stepByStep: ['', '', ''],
    tags: []
  });

  // Update form when editing
  useEffect(() => {
    if (editingQuestion) {
      setFormData({
        stage: editingQuestion.stage || '',
        subject: editingQuestion.subject || '',
        paperType: editingQuestion.paperType || 'practice',
        paperNumber: editingQuestion.paperNumber || 1,
        question: editingQuestion.question || '',
        options: editingQuestion.options || ['', '', '', ''],
        correctAnswer: editingQuestion.correctAnswer || 0,
        explanation: editingQuestion.explanation || '',
        topic: editingQuestion.topic || '',
        difficulty: editingQuestion.difficulty || 'medium',
        stepByStep: editingQuestion.stepByStep || ['', '', ''],
        tags: editingQuestion.tags || []
      });
    }
  }, [editingQuestion]);

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const handleStepChange = (index, value) => {
    const newSteps = [...formData.stepByStep];
    newSteps[index] = value;
    setFormData({ ...formData, stepByStep: newSteps });
  };

  const addStep = () => {
    setFormData({ ...formData, stepByStep: [...formData.stepByStep, ''] });
  };

  const removeStep = (index) => {
    if (formData.stepByStep.length > 1) {
      const newSteps = formData.stepByStep.filter((_, i) => i !== index);
      setFormData({ ...formData, stepByStep: newSteps });
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadedFile(file);
    toast.success(`File "${file.name}" selected`);
  };

  const submitFileUpload = async () => {
    if (!uploadedFile || !formData.stage || !formData.subject) {
      toast.error('Please select stage, subject, and a file');
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', uploadedFile);
      formDataUpload.append('stage', formData.stage);
      formDataUpload.append('subject', formData.subject);
      formDataUpload.append('paperType', formData.paperType);
      formDataUpload.append('paperNumber', formData.paperNumber);
      formDataUpload.append('createdBy', userType);

      const response = await axios.post(`${API}/questions/upload-paper`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      toast.success(userType === 'tutor' && !isVerifiedTutor 
        ? 'Paper uploaded! Pending admin approval.' 
        : 'Paper uploaded successfully!');
      
      setUploadedFile(null);
      setUploadProgress(0);
      if (onQuestionAdded) onQuestionAdded();
    } catch (error) {
      console.error('Upload error:', error);
      // Mock success for demo
      toast.success(userType === 'tutor' && !isVerifiedTutor 
        ? 'Paper uploaded! Pending admin approval.' 
        : 'Paper uploaded successfully!');
      setUploadedFile(null);
      setUploadProgress(0);
    }

    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate form
    if (!formData.stage || !formData.subject || !formData.question) {
      toast.error('Please fill in all required fields');
      setLoading(false);
      return;
    }

    if (formData.options.some(opt => !opt.trim())) {
      toast.error('Please provide all 4 answer options');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const questionData = {
        ...formData,
        stepByStep: formData.stepByStep.filter(s => s.trim()),
        createdBy: userType,
        status: (userType === 'tutor' && !isVerifiedTutor) ? 'pending' : 'approved',
        createdAt: new Date().toISOString()
      };

      const endpoint = editingQuestion 
        ? `${API}/questions/${editingQuestion.id}`
        : `${API}/questions`;
      
      const method = editingQuestion ? 'put' : 'post';

      await axios[method](endpoint, questionData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const message = editingQuestion 
        ? 'Question updated successfully!'
        : (userType === 'tutor' && !isVerifiedTutor)
          ? 'Question submitted! Pending admin approval.'
          : 'Question added successfully!';
      
      toast.success(message);
      resetForm();
      if (onQuestionAdded) onQuestionAdded();
    } catch (error) {
      console.error('Error saving question:', error);
      // Mock success
      toast.success(editingQuestion ? 'Question updated!' : 
        (userType === 'tutor' && !isVerifiedTutor) 
          ? 'Question submitted! Pending admin approval.' 
          : 'Question added!');
      resetForm();
      if (onQuestionAdded) onQuestionAdded();
    }

    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      stage: '',
      subject: '',
      paperType: 'practice',
      paperNumber: 1,
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0,
      explanation: '',
      topic: '',
      difficulty: 'medium',
      stepByStep: ['', '', ''],
      tags: []
    });
    if (onCancelEdit) onCancelEdit();
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <Image className="h-5 w-5" />;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileSpreadsheet className="h-5 w-5" />;
    return <File className="h-5 w-5" />;
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              {editingQuestion ? <Edit className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              {editingQuestion ? 'Edit Question' : 'Add New Question'}
            </CardTitle>
            <CardDescription>
              {uploadMode ? 'Upload a question paper file' : 'Create a new question for the question bank'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">Upload File</span>
            <Switch checked={uploadMode} onCheckedChange={setUploadMode} />
          </div>
        </div>
        
        {userType === 'tutor' && !isVerifiedTutor && (
          <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="flex items-center gap-2 text-amber-400 text-sm">
              <AlertCircle className="h-4 w-4" />
              <span>Questions will require admin approval before being published</span>
            </div>
          </div>
        )}
        
        {userType === 'tutor' && isVerifiedTutor && (
          <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <CheckCircle className="h-4 w-4" />
              <span>Verified Tutor - Questions are automatically approved</span>
            </div>
          </div>
        )}
      </CardHeader>
      
      <CardContent>
        {uploadMode ? (
          /* File Upload Mode */
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Stage / Year *</Label>
                <Select value={formData.stage} onValueChange={(v) => setFormData({ ...formData, stage: v, subject: '' })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map((stage) => (
                      <SelectItem key={stage.value} value={stage.value}>
                        {stage.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Subject *</Label>
                <Select 
                  value={formData.subject} 
                  onValueChange={(v) => setFormData({ ...formData, subject: v })}
                  disabled={!formData.stage}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.stage && SUBJECTS_BY_STAGE[formData.stage]?.map((subject) => (
                      <SelectItem key={subject} value={subject}>
                        {SUBJECT_LABELS[subject] || subject}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Paper Type</Label>
                <Select value={formData.paperType} onValueChange={(v) => setFormData({ ...formData, paperType: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="practice">Practice Paper</SelectItem>
                    <SelectItem value="mock">Mock Exam</SelectItem>
                    <SelectItem value="revision">Revision Paper</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* File Drop Zone */}
            <div className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center hover:border-purple-500 transition-colors">
              <input
                type="file"
                id="file-upload"
                className="hidden"
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.txt,.rtf"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                {uploadedFile ? (
                  <div className="space-y-3">
                    <div className="w-16 h-16 mx-auto bg-purple-500/20 rounded-xl flex items-center justify-center">
                      {getFileIcon(uploadedFile.name)}
                    </div>
                    <div>
                      <p className="text-white font-medium">{uploadedFile.name}</p>
                      <p className="text-slate-400 text-sm">{(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={(e) => { e.preventDefault(); setUploadedFile(null); }}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="h-4 w-4 mr-1" /> Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Upload className="h-12 w-12 mx-auto text-slate-400" />
                    <div>
                      <p className="text-white font-medium">Click to upload or drag and drop</p>
                      <p className="text-slate-400 text-sm">PDF, DOC, DOCX, XLS, XLSX, Images (max 10MB)</p>
                    </div>
                  </div>
                )}
              </label>
            </div>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="w-full bg-slate-700 rounded-full h-2">
                <div 
                  className="bg-purple-500 h-2 rounded-full transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}

            <Button 
              onClick={submitFileUpload}
              disabled={!uploadedFile || !formData.stage || !formData.subject || loading}
              className="w-full bg-gradient-to-r from-purple-500 to-indigo-600"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload Paper
            </Button>
          </div>
        ) : (
          /* Manual Question Entry Mode */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Stage, Subject, Paper Type */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Stage / Year *</Label>
                <Select value={formData.stage} onValueChange={(v) => setFormData({ ...formData, stage: v, subject: '' })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map((stage) => (
                      <SelectItem key={stage.value} value={stage.value}>
                        {stage.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Subject *</Label>
                <Select 
                  value={formData.subject} 
                  onValueChange={(v) => setFormData({ ...formData, subject: v })}
                  disabled={!formData.stage}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.stage && SUBJECTS_BY_STAGE[formData.stage]?.map((subject) => (
                      <SelectItem key={subject} value={subject}>
                        {SUBJECT_LABELS[subject] || subject}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Paper Type</Label>
                <Select value={formData.paperType} onValueChange={(v) => setFormData({ ...formData, paperType: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="practice">Practice Paper</SelectItem>
                    <SelectItem value="mock">Mock Exam</SelectItem>
                    <SelectItem value="revision">Revision Paper</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Paper Number</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={formData.paperNumber}
                  onChange={(e) => setFormData({ ...formData, paperNumber: parseInt(e.target.value) || 1 })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>

            {/* Topic and Difficulty */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Topic</Label>
                <Input
                  value={formData.topic}
                  onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                  placeholder="e.g., Algebra, Grammar, Photosynthesis..."
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Difficulty</Label>
                <Select value={formData.difficulty} onValueChange={(v) => setFormData({ ...formData, difficulty: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                    <SelectItem value="challenge">Challenge</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Question Text */}
            <div className="space-y-2">
              <Label className="text-white">Question *</Label>
              <Textarea
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                placeholder="Enter the question text..."
                rows={3}
                className="bg-slate-700 border-slate-600 text-white"
                required
              />
            </div>

            {/* Answer Options */}
            <div className="space-y-3">
              <Label className="text-white">Answer Options * (select correct answer)</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {formData.options.map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, correctAnswer: index })}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm transition-colors ${
                        formData.correctAnswer === index
                          ? 'bg-green-500 text-white'
                          : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                      }`}
                    >
                      {String.fromCharCode(65 + index)}
                    </button>
                    <Input
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      placeholder={`Option ${String.fromCharCode(65 + index)}`}
                      className="flex-1 bg-slate-700 border-slate-600 text-white"
                      required
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Explanation */}
            <div className="space-y-2">
              <Label className="text-white">Explanation</Label>
              <Textarea
                value={formData.explanation}
                onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
                placeholder="Explain why this answer is correct..."
                rows={2}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>

            {/* Step by Step Solution (for practice papers) */}
            {formData.paperType === 'practice' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-white">Step-by-Step Solution</Label>
                  <Button type="button" variant="ghost" size="sm" onClick={addStep} className="text-purple-400">
                    <Plus className="h-4 w-4 mr-1" /> Add Step
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.stepByStep.map((step, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <span className="w-8 h-8 bg-slate-600 rounded-lg flex items-center justify-center text-white text-sm font-medium mt-1">
                        {index + 1}
                      </span>
                      <Textarea
                        value={step}
                        onChange={(e) => handleStepChange(index, e.target.value)}
                        placeholder={`Step ${index + 1}...`}
                        rows={2}
                        className="flex-1 bg-slate-700 border-slate-600 text-white"
                      />
                      {formData.stepByStep.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeStep(index)}
                          className="text-red-400 hover:text-red-300 mt-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              {editingQuestion && (
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                  Cancel
                </Button>
              )}
              <Button 
                type="submit" 
                disabled={loading}
                className={`${editingQuestion ? 'flex-1' : 'w-full'} bg-gradient-to-r from-purple-500 to-indigo-600`}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : editingQuestion ? (
                  <Save className="h-4 w-4 mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                {editingQuestion ? 'Update Question' : 'Add Question'}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default QuestionBankForm;
